import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Menu, MenuItem, IconButton, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

// React Imports
import { useState, useEffect, useContext, useRef } from 'react';

// Style Imports
import Styles from './NodeIDE.module.scss';
import 'react-toastify/dist/ReactToastify.css';

// Component Imports
import {
    Header,
    StepsDrawer,
    InstructionsPane,
    TerminalPane,
    MultiTabEditor,
    AlertDialog,
    APIDrawer,
    DBViewer
} from "../components";
import validateFiles from "../utility/filesValidator.js";
import validateSqlQuery from '../utility/sqlValidator';
import validateCommand from '../utility/commandValidator';
import validateApiInput from '../utility/apiValidator';
import { Joyride } from 'components/walkthrough/walkthrough';

// Context Imports
import { EditorProvider, MultiTabEditorProvider } from "../contexts";

// Module Imports
import { OpenInNew } from "@mui/icons-material";
import { ToastContainer, toast } from 'react-toastify';

// Util Imports
import { createSocketConnection, cleanupSocket } from '../utils/socketService';
import { decoder } from 'utils/helpers/decoder';
import { copyEnabledChecker } from 'utils/helpers/utility';


// Assets Imports
import Soundcheck from 'assets/sounds/try-again.mp3';
import Sidebar from '../components/CodeEditor/Sidebar/Sidebar';
import { useNavigate, useParams } from 'react-router';
import { AuthContext, useAPI, Feedback } from 'components/lib';
import axios from 'axios';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { Backdrop, LinearProgress, Typography, Box } from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import InfoDrawer from 'components/global/NodeLayout/InfoDrawer';
import { HelperChat } from 'components/global/HelperChat/HelperChat';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';
import { FaClipboard, FaClipboardCheck, FaDatabase, FaTerminal } from 'react-icons/fa';
import { FaBolt } from 'react-icons/fa6';
import { MdOutlinePreview } from 'react-icons/md';
import ContainerLoader from 'components/ContainerLoader/ContainerLoader';

const finalAudio = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/voice/finish_node.mp3`;

export default function NodeIDE(props) {

    const navigate = useNavigate();
    const { node_id } = useParams();
    const { lab_id } = useParams();
    const { resource } = useParams();

    const context = useContext(AuthContext);
    const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`, 'GET');
    const [socketInstance, setSocketInstance] = useState(null);

    //States to track steps and progress
    const [nodeInfo, setNodeInfo] = useState({})
    const [steps, setSteps] = useState();
    const [maxUnlocked, setMaxUnlocked] = useState(0);
    const [nodeFinished, setNodeFinished] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isMuted, setIsMuted] = useState(true);

    //States to track container related tasks
    const [startLab, setStartLab] = useState(true);
    const [loadingContainerStart, setLoadingContainerStart] = useState(false);
    const [loadingScreen, setLoadingScreen] = useState(false)
    const [progress, setProgress] = useState(0);
    const [showReloadConfirm, setShowReloadConfirm] = useState(false);
    const [containerIP, setContainerIP] = useState('')
    const [copied, setCopied] = useState(false);
    const [showFinishConfirm, setShowFinishConfirm] = useState(false);
    const [showContainerError, setShowContainerError] = useState(false);
    const sessionId = localStorage.getItem('sessionId');

    //States to handle user interaction with layout
    const [layoutConfig, setLayoutConfig] = useState({
        showInstructions: true,
        showPreviewDialog: false,
        showTerminal: true,
        showDbViewer: false,
        showStepsDrawer: false,
        showInfoDrawer: false,
        showApiDrawer: false,
        showContainerDomain: false,
        showFinishConfirm: false
    });

    const toggleLayoutState = (key) => {
        setLayoutConfig((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const setLayoutState = (key, value) => {
        setLayoutConfig((prev) => ({ ...prev, [key]: value }));
    };

    //State for tracking files created by users
    const [userFiles, setUserFiles] = useState({});
    const [userApiInput, setUserApiInput] = useState({});
    const [userSqlInput, setUserSqlInput] = useState('');
    const [userCommandHistory, setUserCommandHistory] = useState([]);
    const [userCommandInput, setUserCommandInput] = useState('');

    const isCopyEnabled = copyEnabledChecker(context?.user);

    const addCommandToHistory = (command) => {
        if (command.trim() !== '') {
            setUserCommandHistory(prev => [...prev, command.trim()]);
        }
    };
    useEffect(() => {
        setUserCommandHistory([]);
        setMaxUnlocked(Math.max(currentStep, maxUnlocked));
    }, [currentStep]);

    const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);

    // -----------------------------
    const [anchorEl, setAnchorEl] = useState(null);
    const [codeBlocks, setCodeBlocks] = useState([]);
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopyClick = (event) => {
        resetActivityTimer();
        const content = steps[currentStep]?.instructions || '';
        const extractedBlocks = extractCodeBlocks(content);
        setCodeBlocks(extractedBlocks);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Function to extract code blocks and remove the language name
    const extractCodeBlocks = (content) => {
        const regex = /```(javascript|css|bash)\s*([\s\S]*?)```/g;
        let match;
        const blocks = [];

        while ((match = regex.exec(content)) !== null) {
            blocks.push(match[2].trim());
        }
        return blocks;
    };

    // Function to copy selected code block to clipboard
    const copyToClipboard = (code, index) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopiedIndex(index);
            setTimeout(() => {
                setCopiedIndex(null);
            }, 2000);
        });
    };
    // ----------------------------------------------

    function moveToError() {
        const wastedAttemptAudio = new Audio(Soundcheck);
        wastedAttemptAudio.play();
        toast.dark('The Code does not match the expected input! Try again', {
            type: 'error',
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    function incrementMaxUnlocked() {
        if (maxUnlocked >= 0 && maxUnlocked < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            setNodeFinished(true)
        }
    }

    const handleStepClick = (index) => {
        setCurrentStep(index);
        setLayoutState('showStepsDrawer', false) // Close drawer on selection
    };

    const validator = (type) => {
        switch (type) {
            case 'terminal':
                // Validate Commands
                const expectedInputs = steps[currentStep].exp_inp;
                if (Array.isArray(expectedInputs)) {
                    const inpCount = expectedInputs.length
                    const final_command = userCommandHistory.slice(-inpCount)
                    return expectedInputs.map((expected, index) => {
                        const actual = final_command[index];
                        return validateCommand(actual, expected);
                    }).every(isValid => isValid);
                } else {
                    return validateCommand(userCommandInput, steps[currentStep].exp_inp);
                }
            case 'sql':
                // Validate SQL Queries
                return validateSqlQuery(userSqlInput, steps[currentStep].exp_inp);
            case 'api':
                // Validate API Inputs
                return validateApiInput(userApiInput, steps[currentStep].exp_inp);
            case 'files':
                // Validate File paths and Content
                return validateFiles(userFiles, steps[currentStep].exp_inp);
            default:
                // Defaults to validation of files
                return validateFiles(userFiles, steps[currentStep].exp_inp);
        }
    }

    const handleSubmitClick = () => {
        resetActivityTimer();
        const isEqual = validator(steps[currentStep]?.action_type)
        // const isEqual = true;
        if (isEqual) {
            incrementMaxUnlocked();

            toggleLayoutState('showStepsDrawer');
        } else {
            moveToError()
        }
    }

    const startLabClick = async () => {
        startActivityTimer();
        setLoadingContainerStart(true);
        const formData = {
            resource: nodeInfo?.resource,
        };

        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
            headers: {
                accept: 'application/json',
            },
            data: formData,
        };
        await axios
            .request(options)
            .then((response) => {
                new Promise((resolve) => setTimeout(resolve, 100));

                if (response?.data?.user_ip) {
                    const decodedIP = decoder(response?.data?.user_ip);
                    setContainerIP(decodedIP)
                }

                // Initialize socket connection
                const socket = createSocketConnection(
                    process.env.REACT_APP_RELAY_URL,
                    context.user?.token,
                    () => { setShowContainerError(true) }
                );
                socket.on('connect', () => {
                    if (response.data?.az_copy_command) {
                        socket.emit('azcopy:run', decoder(response.data.az_copy_command));
                    }
                });
                let timeout2 = setTimeout(() => {
                    if (socket && socket?.connected) {
                        socket.emit('tree:refresh');
                        clearTimeout(timeout2);
                    }
                }, 3000);
                setSocketInstance(socket);

                setLoadingContainerStart(false);
                setStartLab(false);
                localStorage.setItem('sessionId', response.data.session_id);
                if (context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id) || nodeInfo.is_assessment === 1) {
                    setIsMuted(!isMuted)
                }
            })
            .catch((err) => {
                setLoadingContainerStart(false);
                if (err.response.status === 403) {
                    navigate('/nopermission');
                } else {
                    navigate('/oops');
                }
            });
    };


    const handleFinish = () => {
        setLoadingScreen(true);
        const formData = {
            sessionId: sessionId,
        };
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
            headers: {
                accept: 'application/json',
            },
            data: {
                ...formData,
                ...(nodeInfo.is_assessment === 1 && { completionPercent: roundedPercentage }),
            },
        };

        axios
            .request(options)
            .then(async function (response) {
                await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
                if (nodeInfo.is_assessment === 1) {
                    setLoadingScreen(false);
                    navigate(`/labs/${lab_id}/nodes`);
                } else {
                    setLoadingScreen(false);
                    navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
                }
                localStorage.removeItem('sessionId');
            })
            .catch((err) => {
                setLoadingScreen(false);
                navigate('/oops');
            });
    };

    const activityTimerRef = useRef(null);

    const startActivityTimer = () => {
        activityTimerRef.current = setTimeout(handleInactivity, 3600000);
    };

    const resetActivityTimer = () => {
        clearTimeout(activityTimerRef.current);
        startActivityTimer();
    };

    function handleInactivity() {
        setShowReloadConfirm(true);
        handleContainerDelete();
    }

    const handleContainerDelete = () => {
        const sessId = localStorage.getItem('sessionId');
        const options = {
            method: 'DELETE',
            url: `/api/container-instances`,
            data: { "sessionId": sessId },

            headers: {
                accept: 'application/json',
            },
        };

        axios
            .request(options)
            .then(async function (response) {
                localStorage.removeItem('sessionId');
            })
            .catch((err) => {
                console.log(err)
            });

    };

    const navigateBack = () => {
        navigate(-1);
    };

    // Tracking activity of user and resetting inactivity timer
    useEffect(() => {
        // Only set up activity tracking when the lab is actually started
        if (!startLab && socketInstance) {
            // Define all the events we want to track
            const eventsToTrack = [
                'mousedown',
                'keydown',
                'touchstart',
                'click',
                'input',
                'change'
            ];

            // Debounce function to prevent excessive timer resets
            let timeoutId;
            const debouncedResetTimer = () => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    resetActivityTimer();
                }, 300); // Wait 300ms before actually resetting the timer
            };

            // Event handler that will be attached to the main container
            const handleActivity = () => {
                debouncedResetTimer();
            };

            // Get the main container element
            const mainContainer = document.getElementById('mainContainer');

            if (mainContainer) {
                // Attach listeners to all specified events
                eventsToTrack.forEach(eventType => {
                    mainContainer.addEventListener(eventType, handleActivity);
                });
            }

            // Cleanup function
            return () => {
                if (mainContainer) {
                    eventsToTrack.forEach(eventType => {
                        mainContainer.removeEventListener(eventType, handleActivity);
                    });
                }
                clearTimeout(timeoutId);
                clearTimeout(activityTimerRef.current);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startLab, socketInstance]);

    const handleVoiceClick = () => {
        resetActivityTimer();
        setIsMuted(!isMuted);
    };

    const handleRefresh = () => {
        window.location.reload();
    };


    useEffect(() => {
        if (data) {
            const jsonData = decoder(data);
            jsonData.numOfSteps = jsonData.steps.steps.length;

            setNodeInfo(jsonData);
            setSteps(jsonData.steps.steps);

            const savedStep = jsonData.userProgress || 0;

            setCurrentStep(savedStep);
            setMaxUnlocked(savedStep);

            document.title = props.t('Labs.Nodes.NodeJenkins.title', { nodeName: jsonData.name });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);



    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupSocket();
        };
    }, []);

    useEffect(() => {
        if (socketInstance) {
            socketInstance.on('disconnect', (reason) => {
                // Show reconnection message to user
                toast.dark('Connection lost. Attempting to reconnect...', {
                    type: 'warning',
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });

                if (reason === 'io server disconnect') {
                    socketInstance.connect();
                }
            });

            socketInstance.on('reconnect', () => {
                toast.success('Reconnected successfully!');
            });

            socketInstance.on('connect_error', () => {
                toast.dark('Failed to reconnect. Please refresh the page.', {
                    type: 'error',
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            });
        }

        return () => {
            if (socketInstance) {
                socketInstance.off('disconnect');
                socketInstance.off('reconnect');
                socketInstance.off('connect_error');
            }
        };
    }, [socketInstance]);

    useEffect(() => {
        if (steps) {
            const audioElement = new Audio(`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`);
            const finalAudioElement = new Audio(finalAudio);
            if (nodeFinished) {
                audioElement?.pause();
                finalAudioElement.play();
            } else if (!isMuted) {
                audioElement?.play();
            }
            return () => {
                audioElement?.pause();
                finalAudioElement.pause();
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isMuted, nodeFinished]);

    useEffect(() => {
        if (loadingContainerStart) {
            const interval = setInterval(() => {
                setProgress((prevProgress) => (prevProgress >= 95 ? 100 : prevProgress + 0.5));
            }, 1000);

            if (!loadingContainerStart) {
                clearInterval(interval);
            }
            return () => {
                clearInterval(interval);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingContainerStart]);


    const sendHeartbeat = () => {
        const formData = {
            node_id: node_id,
        };
        const options = {
            method: 'POST',
            url: `/api/container-instances/heartbeat`,
            headers: {
                accept: 'application/json',
            },
            data: formData,
        };

        axios
            .request(options)
            .then(function (response) {
            })
            .catch((err) => {
            });
    };

    useEffect(() => {
        //send heartbeat call every 4 mins
        const heartBeatId = setInterval(sendHeartbeat, 240000);

        return () => {
            clearInterval(heartBeatId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const editorValue = {
        socket: socketInstance,
        userFiles,
        setUserFiles,
        setUserCommandInput,
        setUserSqlInput,
        setUserApiInput,
        addCommandToHistory,
        rootPath: nodeInfo?.resource?.config?.rootPath
    }

    const activityBarItems = [
        { id: 'terminalToggle', icon: <FaTerminal size='18px' />, title: 'Terminal', action: () => toggleLayoutState('showTerminal'), disabled: false },

        { id: 'previewToggle', icon: <MdOutlinePreview size='18px' />, title: 'Preview', action: () => toggleLayoutState('showPreviewDialog'), disabled: !nodeInfo?.resource?.config?.previewToggle },

        { id: 'apiTool', icon: <FaBolt size='18px' />, title: 'API Tool', action: () => toggleLayoutState('showApiDrawer'), disabled: !nodeInfo?.resource?.config?.apiTool },

        { id: 'dbViewer', icon: <FaDatabase size='18px' />, title: 'DB Viewer', action: () => setLayoutState('showDbViewer', true), disabled: !nodeInfo?.resource?.config?.dbViewer },
    ];

    const ipDialogText = () => {
        const handleCopy = () => {
            resetActivityTimer();
            navigator.clipboard.writeText(`http://${containerIP}:3000`).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
            });
        };

        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: 'space-between', width: '100%', backgroundColor: '#efefef', padding: '1em 0.5em', borderRadius: '8px' }}>
                <span style={{ fontWeight: '600', color: '#1450a0' }}>{`http://${containerIP}:3000`}</span>
                <button
                    onClick={handleCopy}
                    title={copied ? 'copied' : 'copy'}
                    style={{
                        cursor: "pointer",
                        padding: "5px 10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px"
                    }}
                >
                    {copied ? <FaClipboardCheck /> : <FaClipboard />}
                </button>
            </div>
        );
    }

    const resetWalkthrough = (data) => {
        if ((data.status === 'finished') | (data.action === 'skip') | (data.action === 'tour:end')) {
            setIsMuted(!isMuted);
        }
    };

    return (
        <>
            {loading || !steps || loadingScreen ? (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : (
                <>
                    <ConfirmationCard
                        title={nodeInfo.is_assessment ? "Start Assessment?" : "Start Course?"}
                        body={"Your container will take some time to initialize, please be patient. Press 'Confirm' to initiate the process. NOTE: If you refresh or close the session, you'll lose your progress."}
                        showCard={startLab}
                        setShowCard={setStartLab}
                        handleCancel={navigateBack}
                        handleConfirm={startLabClick}
                    />
                    {loadingContainerStart && (
                        <ContainerLoader 
                            progress={progress} 
                            loadingContainerStart={loadingContainerStart} 
                            containerHints={nodeInfo?.container_loader_json} 
                        />
                    )}
                    
                    {socketInstance && !startLab && (

                        <>
                            {!startLab && !nodeInfo.is_assessment !== 1 ? (
                                <Joyride
                                    open={nodeInfo?.resource?.walkthrough?.open}
                                    run={nodeInfo?.resource?.walkthrough?.run}
                                    steps={nodeInfo?.resource?.walkthrough?.steps || []}
                                    resourceId={nodeInfo?.resource?.lab_resource_id}
                                    disableOverlay={true}
                                    callback={resetWalkthrough}
                                />
                            ) : null}
                            <ToastContainer
                                id="warning-toast"
                                position="top-right"
                                autoClose={5000}
                                hideProgressBar={false}
                                newestOnTop
                                closeOnClick
                                rtl={false}
                                pauseOnFocusLoss
                                draggable
                                pauseOnHover
                                limit={3}
                            />
                            <ConfirmationCard
                                title={props.t("Labs.Nodes.NodeTerminal.confirm.timeout.title")}
                                body={props.t("Labs.Nodes.NodeTerminal.confirm.timeout.body")}
                                showCard={showReloadConfirm}
                                setShowCard={setShowReloadConfirm}
                                handleCancel={navigateBack}
                                handleConfirm={handleRefresh}
                                confirmButtonText={props.t('Labs.Nodes.NodeJenkins.confirm.button.refresh')}
                                cancelButtonText={props.t('Labs.Nodes.NodeJenkins.confirm.button.goBack')}
                            />
                            <ConfirmationCard
                                title={"Connection Error"}
                                body={"There seems to be a connection error. Please refresh the page to restart the course"}
                                showCard={showContainerError}
                                setShowCard={setShowContainerError}
                                handleCancel={navigateBack}
                                handleConfirm={handleRefresh}
                                confirmButtonText={"Refresh"}
                                cancelButtonText={"Go Back"}
                            />

                            <BackConfirmationCard
                                isSaveAvailable={false}
                                isContainerLab={true}
                                sessionId={sessionId}
                            />
                            <div id='mainContainer' className={Styles.mainContainer} >
                                <EditorProvider value={editorValue}>
                                    <Header
                                        onDrawerToggle={() => toggleLayoutState('showStepsDrawer')}
                                        toggleInstructions={() => toggleLayoutState('showInstructions')}
                                        toggleInfoDrawer={() => toggleLayoutState('showInfoDrawer')}
                                        currentTimeline={steps[currentStep]?.timeline}
                                        setCurrentStep={setCurrentStep}
                                        currentStep={currentStep}
                                        maxUnlocked={maxUnlocked}
                                        setMaxUnlocked={setMaxUnlocked}
                                        nodeFinished={nodeFinished}
                                        handleSubmitClick={handleSubmitClick}
                                        handleFinishClick={handleFinish}
                                        setShowFinishConfirm={setShowFinishConfirm}
                                        nodeTitle={nodeInfo?.name}
                                        setShowContainerIP={setLayoutState}
                                        sessionId={sessionId}
                                        resource={nodeInfo?.resource}
                                        lab_id={lab_id}
                                        node_id={node_id}
                                        steps={steps}
                                        data={data}
                                        initialUserProgress={nodeInfo.userProgress}
                                    />

                                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                                        {codeBlocks.length > 0 ? (
                                            codeBlocks.map((block, index) => (
                                                <MenuItem key={index} onClick={() => copyToClipboard(block, index)}>
                                                    Code Block {index + 1}
                                                    <Tooltip title="Copy">
                                                        <span>
                                                            <IconButton size="small">
                                                                {copiedIndex === index ? (
                                                                    <CheckIcon color="success" />
                                                                ) : (
                                                                    <ContentCopyIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem style={{ cursor: 'auto' }} >No code blocks found</MenuItem>
                                        )}
                                    </Menu>

                                    <StepsDrawer
                                        open={layoutConfig.showStepsDrawer}
                                        onClose={() => toggleLayoutState('showStepsDrawer')}
                                        steps={steps}
                                        onStepClick={handleStepClick}
                                        totalSteps={nodeInfo?.numOfSteps}
                                        currentStep={currentStep}
                                        nodeFinished={nodeFinished}
                                        maxUnlocked={maxUnlocked}
                                    />
                                    <MultiTabEditorProvider>
                                        <div id="mainLayout" className={Styles.mainLayout} >
                                            <div id='activityBar' className={Styles.activityBar}>
                                                {activityBarItems.map((item) => (
                                                    <button id={item.id} title={item.title} onClick={item.action} disabled={item.disabled}>
                                                        {item.icon}
                                                    </button>
                                                ))}
                                            </div>
                                            <Sidebar />
                                            <div id='ideContainer' className={Styles.ideContainer}>
                                                <MultiTabEditor showToolsPane={layoutConfig.showTerminal} />
                                                <div className={Styles.terminalPane} style={{ display: layoutConfig.showTerminal ? '' : 'none' }}>
                                                    <TerminalPane showToolsPane={layoutConfig.showTerminal} showInstructions={layoutConfig.showInstructions} resetActivityTimer={resetActivityTimer} />
                                                </div>
                                            </div>
                                            {layoutConfig.showInstructions &&
                                                <InstructionsPane
                                                    content={steps[currentStep]?.instructions}
                                                    isMuted={isMuted}
                                                    handleVoiceClick={handleVoiceClick}
                                                    toggleInstructions={() => { toggleLayoutState('showInstructions') }}
                                                    showInstructions={layoutConfig.showInstructions}
                                                    copyButton={
                                                        <IconButton onClick={handleCopyClick}>
                                                            <ContentCopyIcon />
                                                        </IconButton>
                                                    }
                                                    isCopyEnabled={isCopyEnabled}
                                                />
                                            }
                                            <AlertDialog
                                                dialogOpen={layoutConfig.showPreviewDialog}
                                                dialogTitle={`Open Preview`}
                                                dialogText={`The preview will open in a new tab. Please ensure the client is running for the preview to appear`}
                                                confirmButtonText={'Open'}
                                                confirmButtonIcon={<OpenInNew fontSize="small" sx={{ mr: '1px' }} />}
                                                onConfirm={() => {
                                                    window.open(`http://${containerIP}:5173`, '_blank');
                                                    toggleLayoutState('showPreviewDialog');
                                                }}
                                                cancelButtonText={"Cancel"}
                                                onCancel={() => { toggleLayoutState('showPreviewDialog') }}
                                            />
                                            <AlertDialog
                                                dialogOpen={layoutConfig.showContainerDomain}
                                                dialogTitle={`Domain For API`}
                                                dialogText={ipDialogText()}
                                                confirmButtonText={'Close'}
                                                onConfirm={() => {
                                                    setLayoutState('showContainerDomain', false);
                                                }}
                                                onCancel={() => {
                                                    setLayoutState('showContainerDomain', false);
                                                }}
                                            />
                                            {(nodeInfo?.resource?.config?.apiTool) && (
                                                <APIDrawer
                                                    drawerOpen={layoutConfig.showApiDrawer}
                                                    onClose={() => toggleLayoutState('showApiDrawer')}
                                                    resetActivityTimer={resetActivityTimer}
                                                />
                                            )}
                                        </div>
                                        {nodeInfo?.resource?.config?.dbPath && (
                                            <Backdrop
                                                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
                                                open={layoutConfig.showDbViewer}
                                                invisible={!layoutConfig.showDbViewer}
                                            >
                                                <DBViewer setShowDBViewer={setLayoutState} dbPath={nodeInfo?.resource?.config?.dbPath} />
                                            </Backdrop>
                                        )}
                                    </MultiTabEditorProvider>
                                </EditorProvider>
                            </div >
                            <ConfirmationCard
                                title={props.t('Labs.Nodes.NodeJenkins.confirm.completed.title')}
                                body={props.t('Labs.Nodes.NodeJenkins.confirm.completed.body')}

                                showCard={showFinishConfirm}
                                setShowCard={setShowFinishConfirm}
                                handleConfirm={handleFinish}
                            />
                            <InfoDrawer
                                handleDrawer={() => toggleLayoutState('showInfoDrawer')}
                                drawerOpen={layoutConfig.showInfoDrawer}
                                titleText={props.t('Labs.Nodes.NodeJenkins.reference', { LabName: nodeInfo.labName })}
                                displayData={nodeInfo?.content?.reference_data}
                            />
                            {(!startLab && !nodeFinished && nodeInfo.is_assessment !== 1 && nodeInfo?.resource?.config?.chatbotTopic) && (
                                <HelperChat
                                    active={props.aiChat}
                                    topic={nodeInfo?.resource?.config?.chatbotTopic}
                                />
                            )}
                            {nodeFinished && <Feedback isInNode={true} nodeName={nodeInfo.name} labName={nodeInfo.labName} />}
                        </>
                    )}
                </>
            )}
        </>
    )

}
